<template>
  <div class="modal-overlay" @click.self="requestClose">
    <div class="modal-container">

      <button class="modal-close" @click=closeModal>X</button>

      <div class="modal-image-section">
        <img v-if="recipe.imageUrl" :src="recipe.imageUrl" :alt="recipe.name" class="modal-image" />
        <div class="modal-tags">
          <span v-for="tag in recipe.tags" :key="tag.id" class="modal-tag">{{ tag.name }}</span>
          <span v-if="recipe.vegan" class="modal-tag">{{ $t('health.recipe.vegan') }}</span>
          <span v-if="recipe.vegetarian && !recipe.vegan" class="modal-tag">{{ $t('health.recipe.vegetarian') }}</span>
          <button v-if="!recipe.isEdited && recipe.imageUrl != null" class="like-button" @click.stop="toggleLike">
            <font-awesome-icon
              :icon="liked ? faHeartSolid : faHeart"
              :class="{ liked: liked }"
            />
          </button>
        </div>

      </div>

      <div class="modal-body">
        <p class="modal-meal-type">{{ $t(`health.nutritionalPlan.mealTypes2.${recipe.type}`)}}</p>
        <h2 class="modal-title">{{ recipe.name }}</h2>

        <div class="modal-section">
        <h3 class="modal-section-title">{{ $t('health.summary.macroDistribution') }}</h3>
        <div class="macro-chart-row">
          <ingest-macro-distribution-chart
            :ingredients="localItems || []"
            :height="280"
            :width="280"
          />
          <div class="macro-summary">
            <div class="macro-summary__item">
              <span class="macro-summary__label">{{ $t('health.summary.protein') }}</span>
              <span class="macro-summary__value">{{ totalMacro('protein') }}g</span>
              <span class="macro-summary__percent" style="background: #9765E0">{{ proteinPercent() }}%</span>
            </div>
            <div class="macro-summary__item">
              <span class="macro-summary__label">{{ $t('health.summary.carbs') }}</span>
              <span class="macro-summary__value">{{ totalMacro('carbs') }}g</span>
              <span class="macro-summary__percent" style="background: #C4CC52">{{ carbsPercent() }}%</span>
            </div>
            <div class="macro-summary__item">
              <span class="macro-summary__label">{{ $t('health.summary.fat') }}</span>
              <span class="macro-summary__value">{{ totalMacro('fat') }}g</span>
              <span class="macro-summary__percent" style="background: #00C2BA">{{ fatPercent() }}%</span>
            </div>
            <div class="macro-summary__item macro-summary__item--fibre">
              <span class="macro-summary__label">{{ $t('health.summary.fibre') }}</span>
              <span class="macro-summary__value">{{ totalMacro('fibre') }}g</span>
            </div>
          </div>
        </div>
      </div>
        <template>
          <div class="modal-section">
            <div class="score-bar-container">
              <p class="score-bar__label">{{ $t('health.summary.glucoscore') }}</p>

              <div class="score-row">
                <div class="score-bar-wrapper">
                  <div class="score-bar">
                    <div class="score-bar__dim" />
                    <div
                      class="score-bar__highlight"
                      :style="activeZoneStyle"
                    />
                    <div
                      class="score-bar__marker"
                      :style="{
                left: markerPosition + '%',
                borderColor: predictedScoreColor,
                boxShadow: `0 0 0 3px ${predictedScoreColor}33`
              }"
                    >
                      <div class="score-bar__marker-inner" :style="{ background: predictedScoreColor }" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <div class="isp-value">
          <strong>ISP: {{ predictedIsp.toFixed(2) }} %</strong>
        </div>

        <GlucoseCurveChart
          v-if="glucoseCurve"
          :data="glucoseCurve"
          :color="predictedScoreColor"
        />

        <div class="modal-section">
          <h3 class="modal-section-title">{{ $t('health.nutritionalPlan.ingredients') }}</h3>
          <food-table
            :items="localItems || []"
            :no-weight="true"
            :show-fibre="true"
            @updateAmount="onUpdateAmount"
            @delete="onDeleteFood"
          />
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import IngestMacroDistributionChart from 'BRAND/components/nutritionPlan/IngestMacroDistributionChart';
import FoodTable from 'BRAND/components/nutritionPlan/FoodTable';
import { faHeart } from '@fortawesome/pro-regular-svg-icons'
import { faHeart as faHeartSolid } from '@fortawesome/pro-solid-svg-icons'
import FontAwesomeIcon from '@fortawesome/vue-fontawesome'
import GlucoseCurveChart from "./GlucoseCurveChart.vue";
import recipeApi from "../../api/recipe";
import {mapGetters} from "vuex";

export default {
  name: 'RecipeDetailModal',
  components: {
    IngestMacroDistributionChart,
    FoodTable,
    FontAwesomeIcon,
    GlucoseCurveChart,

  },
  props: {
    recipe: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    const items = JSON.parse(JSON.stringify(this.recipe.items || []));

    const originalAmounts = {};

    items.forEach(item => {
      // Plan items may arrive with the food id keyed as either food_id (most of the app)
      // or foodId (the plan endpoint's RecipeRecordV2.Item is serialized camelCase). Key the
      // baseline consistently so change-detection in closeModal can find it; otherwise an
      // edit is treated as "no change" and silently discarded.
      const foodKey = item.food_id || item.foodId;
      originalAmounts[foodKey] = {
        amount:   item.amount,
        calories: item.calories,
        protein:  item.protein,
        carbs:    item.carbs,
        fat:      item.fat,
        fibre:    item.fibre || 0,
      };
    });
    return {
      originalAmounts,
      faHeart,
      faHeartSolid,
      predictedIsp: 0,
      glucoseCurve: null,
      predictedScoreColor: null,
      predictedScore: 0,
      loadingDetail: false,
      liked:false,
      localItems:items,
      loadedForId: null,
      errorForId: null,
    };
  },computed: {
    ...mapGetters([
      'getLanguageCode'
    ]),
    // El glucoscore crudo puede salir negativo (recetas tipo aguacate
    // con poca carb + mucha fibra) o por encima de 100 (recetas muy
    // carbohidratadas con penalizacion - llegamos a ver 213.533 con
    // arroz+patata). Si no se acota, la posicion del marker se sale
    // del contenedor. Acotamos a 1-100 para el numero visible y para
    // el calculo de la posicion.
    clampedScore () {
      return Math.max(1, Math.min(100, Math.round(this.predictedScore)));
    },
    // La barra tiene un solo color (el de la categoria de la receta).
    // El marker se posiciona como (100 - clampedScore) para que recetas
    // malas (score alto) queden a la izquierda y buenas (score bajo) a
    // la derecha. Mantiene la semantica izq=malo / der=bueno aunque la
    // barra no muestre el gradiente completo.
    markerPosition () {
      return 100 - this.clampedScore;
    },
    // Estilo para la capa highlight: recorta el gradiente completo
    // dejando ver solo el tercio donde cae el marker. La barra inferior
    // (.score-bar__dim) sigue mostrando el gradiente completo al 30%
    // para mantener el contexto visual. Tercios: 0-33 roja, 33-66
    // amarilla, 66-100 verde.
    activeZoneStyle () {
      const pos = this.markerPosition;
      let left = 0;
      let right = 100;
      if (pos < 33.33) {
        left = 0;
        right = 100 - 33.33;
      } else if (pos < 66.66) {
        left = 33.33;
        right = 100 - 66.66;
      } else {
        left = 66.66;
        right = 0;
      }
      return {
        clipPath: `inset(0 ${right}% 0 ${left}%)`
      };
    }
  }, watch: {
    'recipe.id': {
      immediate: true,
      async handler(newId) {
        if (
          !newId ||
          this.loadingDetail ||
          this.loadedForId === newId ||
          this.errorForId === newId
        ) return;

        this.loadingDetail = true;

        try {

          if (this.recipe.isEdited) {
            await this.$nextTick();
            const extraInfo = await recipeApi.getPredictedScore(
              this,
              parseFloat(this.totalMacro('protein')),
              parseFloat(this.totalMacro('fat')),
              parseFloat(this.totalMacro('carbs')),
              parseFloat(this.totalMacro('fibre'))
            );

            this.predictedIsp        = extraInfo.isp;
            this.glucoseCurve        = extraInfo.glucoseCurve;
            this.predictedScoreColor = extraInfo.color;
            this.predictedScore      = extraInfo.score;

          } else {
            const lang = typeof this.getLanguageCode === 'function'
              ? this.getLanguageCode()
              : this.getLanguageCode;

            const detail = await recipeApi.getRecipeDetail(this, newId, lang);
              this.predictedIsp        = detail.predictedIsp;
              this.glucoseCurve        = detail.glucoseCurve;
              this.predictedScoreColor = detail.predictedScoreColor;
              this.predictedScore      = detail.predictedScore;
              this.liked = await recipeApi.isFavorite(this, newId);
          }

          this.loadedForId = newId;
          this.errorForId = null;

        } catch (e) {
          console.error('Error fetching recipe detail:', e);
          this.errorForId = newId;

        } finally {
          this.loadingDetail = false;
        }
      }
    }
  },
  methods: {
    async onUpdateAmount(updatedFood) {
      const updatedKey = updatedFood.food_id || updatedFood.foodId;
      const index = this.localItems.findIndex(i => (i.food_id || i.foodId) === updatedKey);
      if (index === -1) return;

      const item = this.localItems[index];
      const original = this.originalAmounts[item.food_id || item.foodId];
      if (!original) return;

      const ratio = updatedFood.amount / original.amount;

      this.$set(this.localItems, index, {
        ...item,
        calories: parseFloat((original.calories * ratio).toFixed(2)),
        protein:  parseFloat((original.protein  * ratio).toFixed(2)),
        carbs:    parseFloat((original.carbs    * ratio).toFixed(2)),
        fat:      parseFloat((original.fat      * ratio).toFixed(2)),
        fibre:    parseFloat((original.fibre    * ratio).toFixed(2)),
        amount:   updatedFood.amount,
      });
      const extraInfo = await recipeApi.getPredictedScore(this, parseFloat(this.totalMacro('protein')), parseFloat(this.totalMacro('fat')), parseFloat(this.totalMacro('carbs')),parseFloat(this.totalMacro('fibre')));
      this.predictedIsp        = extraInfo.isp;
      this.glucoseCurve        = extraInfo.glucoseCurve;
      this.predictedScoreColor = extraInfo.color;
      this.predictedScore      = extraInfo.score;

    },
    async onDeleteFood(food) {
      const deleteKey = food.food_id || food.foodId;
      const index = this.localItems.findIndex(i => (i.food_id || i.foodId) === deleteKey);
      if (index !== -1) {
        this.$delete(this.localItems, index);
      }
      const extraInfo = await recipeApi.getPredictedScore(this, parseFloat(this.totalMacro('protein')), parseFloat(this.totalMacro('fat')), parseFloat(this.totalMacro('carbs')),parseFloat(this.totalMacro('fibre')));
      this.predictedIsp        = extraInfo.isp;
      this.glucoseCurve        = extraInfo.glucoseCurve;
      this.predictedScoreColor = extraInfo.color;
      this.predictedScore      = extraInfo.score;
    },async toggleLike() {
      if (this.liked) {
        await recipeApi.removeFavorite(this, this.recipe.id)
        this.liked = false;
      } else {
        await recipeApi.addFavorite(this, this.recipe.id)
        this.liked = true;

      }
    },
    totalMacro(macro) {
      if (!this.localItems) return 0;
      return this.localItems.reduce((sum, item) => sum + (item[macro] || 0), 0).toFixed(2);
    },
    getTotalKcals() {
      return parseFloat(this.localItems.reduce((acc, i) => acc + i.calories, 0).toFixed(2));
    },
    proteinPercent() {
      const kcals = this.getTotalKcals();
      if (!kcals) return 0;
      return Math.round(parseFloat(this.totalMacro('protein')) * 4 / kcals * 100);
    },
    carbsPercent() {
      const kcals = this.getTotalKcals();
      if (!kcals) return 0;
      return Math.round(parseFloat(this.totalMacro('carbs')) * 4 / kcals * 100);
    },
    fatPercent() {
      const kcals = this.getTotalKcals();
      if (!kcals) return 0;
      return Math.round(parseFloat(this.totalMacro('fat')) * 9 / kcals * 100);
    },
    hasChanges() {
      return this.localItems.length !== this.recipe.items.length ||
        this.localItems.some(item => {
          const original = this.originalAmounts[item.food_id || item.foodId];
          return original && item.amount !== original.amount;
        });
    },
    // Clicking the dark backdrop used to emit close() with NO payload, so the calendar's
    // onRecipeModalClose received undefined, skipped the write-back and threw away every
    // edit without a word. Dismissing a dialog by clicking outside it is a normal reflex,
    // so the discard stays available — it just has to be confirmed now instead of being
    // the surprise default.
    requestClose() {
      if (this.hasChanges() && !confirm(this.$t('health.nutritionalPlan.discardRecipeChanges'))) {
        return;
      }
      this.$emit('close', null);
    },
    closeModal() {
      if (this.hasChanges()) {
        this.$emit('close', this.localItems);
      } else {
        this.$emit('close', null);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "src/stylesheets/variables";

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba($black, 0.6);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-container {
  background: $white;
  border-radius: $radius;
  width: 1100px;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.modal-close {
  position: sticky;
  top: 0.5em;
  float: right;
  margin: 0.5em;
  background: $primary;
  color: $white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
  font-size: 1em;
  z-index: 10;

  &:hover {
    background: $secondary;
  }
}

.modal-image {
  width: 100%;
  max-height: 250px;
  object-fit: cover;
}

.modal-tags {
  display: flex;
  flex-wrap: wrap;
  align-items : center;
  gap: 0.5em;
  padding: 0.75em 1em;
}

.modal-tag {
  background: rgba($primary, 0.1);
  color: $primary;
  border: 1px solid rgba($primary, 0.3);
  border-radius: 12px;
  padding: 0.2em 0.8em;
  font-size: 0.8em;
}

.modal-body {
  padding: 1em 1.5em 1.5em;
}

.modal-meal-type {
  color: $black-light5;
  font-size: 0.85em;
  margin: 0 0 0.25em 0;
  text-transform: uppercase;
}

.modal-title {
  color: $black;
  font-size: 1.4em;
  font-weight: bold;
  margin: 0 0 1.5em 0;
}

.modal-section {
  margin-bottom: 2em;
}

.modal-section-title {
  color: $primary;
  font-weight: bold;
  margin: 0 0 0.75em 0;
  font-size: 1em;
  border-bottom: 1px solid $black-light8;
  padding-bottom: 0.5em;
}
.like-button {
  background: none;
  border: none;
  padding: 0;
  margin-left: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 16px;
  line-height: 1;
}

.like-button:hover {
  transform: scale(1.1);
}

.liked {
  color: $primary
}

.isp-value strong {
  font-size: 1rem;
}
.macro-chart-row {
  display: flex;
  align-items: center;
  gap: 1.5em;
}

.macro-summary {
  display: flex;
  flex-direction: column;
  gap: 0.75em;
  flex: 1;

  &__item {
    display: flex;
    align-items: center;
    gap: 0.5em;

    &--fibre {
      margin-top: 0.5em;
      padding-top: 0.5em;
      border-top: 1px solid $black-light8;
    }
  }

  &__label {
    flex: 1;
    font-size: 0.9em;
    color: $black-light4;
    font-weight: bold;

  }

  &__value {
    font-size: 0.9em;
    min-width: 3em;
    text-align: right;
  }

  &__percent {
    color: $white;
    font-size: 0.8em;
    padding: 0.15em 0.5em;
    border-radius: 12px;
    min-width: 3em;
    text-align: center;
  }
}

.score-bar-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.score-bar__label {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #aaa;
  margin: 0;
}

.score-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.score-value {
  font-size: 1rem;
  font-weight: 700;
  font-family: 'Outfit', 'Segoe UI', sans-serif;
  line-height: 1;
  min-width: 64px;
  text-align: center;
  transition: color 0.3s ease;
}

.score-bar-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.score-bar__legend {
  display: flex;
  justify-content: space-between;
  font-size: 0.7rem;
  color: #bbb;
}

.score-bar {
  position: relative;
  width: 100%;
  height: 10px;
  background: #f0f0f0;
  border-radius: 99px;
  overflow: visible;
}

.score-bar__dim,
.score-bar__highlight {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(to right, #e63939 0%, #ffc107 50%, #4caf7d 100%);
}

.score-bar__dim {
  opacity: 0.3;
}

.score-bar__highlight {
  opacity: 1;
  transition: clip-path 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.score-bar__marker {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  border: 2.5px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: left 0.6s cubic-bezier(0.4, 0, 0.2, 1),
  border-color 0.3s ease,
  box-shadow 0.3s ease;
  z-index: 2;
}

.score-bar__marker-inner {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  transition: background 0.3s ease;
}

.score-bar__scale {
  display: flex;
  justify-content: space-between;
  font-size: 0.68rem;
  color: #ccc;
}

</style>



// WEBPACK FOOTER //
// src/brand/health/components/nutritionPlan/RecipeDetailModal.vue